import type { WireByCellKey } from 'store/useCrossReferenceStore/types';
import type { CrossReferenceWireEntry, CrossReferenceWireMap } from '../types';

// Groups dirty cross-ref entries by parent scope so callers can fold each
// group into the right nested payload at save time. See design spec §11.4.
export interface DistributedPayload {
  // cellKeys of the 'captable:<id>:*' entries, grouped by parent id.
  capTableByParentId: Record<string, CrossReferenceWireMap>;
  // cellKeys of the 'fundownershipdetail:<id>:*' entries, grouped by parent id.
  fundOwnershipDetailByParentId: Record<string, CrossReferenceWireMap>;
}

// Parse a cellKey of the form `${parentType}:${parentId}:${fieldAlias}`.
const parseCellKey = (cellKey: string): { parentType: string; parentId: string; fieldAlias: string } | null => {
  const firstColon = cellKey.indexOf(':');
  if (firstColon < 0) {
    return null;
  }
  const secondColon = cellKey.indexOf(':', firstColon + 1);
  if (secondColon < 0) {
    return null;
  }
  return {
    parentType: cellKey.slice(0, firstColon),
    parentId: cellKey.slice(firstColon + 1, secondColon),
    fieldAlias: cellKey.slice(secondColon + 1),
  };
};

export const distributeDirtyPayload = (payload: WireByCellKey): DistributedPayload => {
  const capTableByParentId: Record<string, CrossReferenceWireMap> = {};
  const fundOwnershipDetailByParentId: Record<string, CrossReferenceWireMap> = {};

  Object.entries(payload).forEach(([cellKey, entry]) => {
    const parsed = parseCellKey(cellKey);
    if (!parsed) {
      return;
    }
    const { parentType, parentId, fieldAlias } = parsed;

    if (parentType === 'captable') {
      capTableByParentId[parentId] = capTableByParentId[parentId] ?? {};
      capTableByParentId[parentId][fieldAlias] = entry as CrossReferenceWireEntry;
    } else if (parentType === 'fundownershipdetail') {
      fundOwnershipDetailByParentId[parentId] = fundOwnershipDetailByParentId[parentId] ?? {};
      fundOwnershipDetailByParentId[parentId][fieldAlias] = entry as CrossReferenceWireEntry;
    }
    // Unknown parentType: silently skip. Callers should never be asked to
    // route entries they don't know how to fold.
  });

  return { capTableByParentId, fundOwnershipDetailByParentId };
};
